/**
 * SoundSystem — Web Audio API wrapper with two independent channels:
 *   music  : looping background tracks
 *   sfx    : one-shot sound effects
 *
 * Signal chain per channel:
 *   source → muteGain (0 or 1) → volumeGain (0–1) → destination
 *
 * Settings are persisted in localStorage so they survive page reloads.
 */

/**
 * Read a 0–1 volume setting from localStorage. Clamps corrupt / non-finite
 * values to the default rather than letting `NaN` flow into a GainNode (which
 * silently mutes the whole channel until the setting is rewritten).
 */
function readVolume(key: string, fallback: number): number {
  const raw = localStorage.getItem(key);
  if (raw === null) return fallback;
  const v = parseFloat(raw);
  if (!Number.isFinite(v)) return fallback;
  return Math.max(0, Math.min(1, v));
}

export class SoundSystem {
  private ctx: AudioContext;

  private musicVolumeGain: GainNode;
  private musicMuteGain: GainNode;
  private sfxVolumeGain: GainNode;
  private sfxMuteGain: GainNode;

  private currentMusic: AudioBufferSourceNode | null = null;
  private currentMusicUrl: string | null = null;
  private bufferCache = new Map<string, AudioBuffer>();

  private _musicVolume: number;
  private _sfxVolume: number;
  private _musicMuted: boolean;
  private _sfxMuted: boolean;
  private _paused = false;

  constructor() {
    this.ctx = new AudioContext();

    this._musicVolume = readVolume('snd_music_vol', 0.5);
    this._sfxVolume   = readVolume('snd_sfx_vol',   0.8);
    this._musicMuted  = localStorage.getItem('snd_music_muted') === 'true';
    this._sfxMuted    = localStorage.getItem('snd_sfx_muted')   === 'true';

    // Music chain
    this.musicVolumeGain = this.ctx.createGain();
    this.musicVolumeGain.gain.value = this._musicVolume;
    this.musicVolumeGain.connect(this.ctx.destination);

    this.musicMuteGain = this.ctx.createGain();
    this.musicMuteGain.gain.value = this._musicMuted ? 0 : 1;
    this.musicMuteGain.connect(this.musicVolumeGain);

    // SFX chain
    this.sfxVolumeGain = this.ctx.createGain();
    this.sfxVolumeGain.gain.value = this._sfxVolume;
    this.sfxVolumeGain.connect(this.ctx.destination);

    this.sfxMuteGain = this.ctx.createGain();
    this.sfxMuteGain.gain.value = this._sfxMuted ? 0 : 1;
    this.sfxMuteGain.connect(this.sfxVolumeGain);
  }

  // ── Getters ────────────────────────────────────────────────
  get musicVolume() { return this._musicVolume; }
  get sfxVolume()   { return this._sfxVolume; }
  get musicMuted()  { return this._musicMuted; }
  get sfxMuted()    { return this._sfxMuted; }

  /** Expose internals needed by procedural audio (MotorSound) */
  getAudioContext(): AudioContext { return this.ctx; }
  getSfxMuteGain(): GainNode { return this.sfxMuteGain; }

  // ── Setters ────────────────────────────────────────────────
  setMusicVolume(v: number) {
    this._musicVolume = Math.max(0, Math.min(1, v));
    this.musicVolumeGain.gain.value = this._musicVolume;
    localStorage.setItem('snd_music_vol', String(this._musicVolume));
  }

  setSfxVolume(v: number) {
    this._sfxVolume = Math.max(0, Math.min(1, v));
    this.sfxVolumeGain.gain.value = this._sfxVolume;
    localStorage.setItem('snd_sfx_vol', String(this._sfxVolume));
  }

  setMusicMuted(m: boolean) {
    this._musicMuted = m;
    this.musicMuteGain.gain.value = m ? 0 : 1;
    localStorage.setItem('snd_music_muted', String(m));
  }

  setSfxMuted(m: boolean) {
    this._sfxMuted = m;
    this.sfxMuteGain.gain.value = m ? 0 : 1;
    localStorage.setItem('snd_sfx_muted', String(m));
  }

  // ── AudioContext unlock (must be called after a user gesture) ──
  resume() {
    if (this._paused) return;
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  /**
   * Pause all audio: suspend the context and make playMusic/playSound no-ops
   * until unpaused. Used to keep audio silent while the tab is hidden even
   * though game systems keep ticking and may try to trigger new sounds.
   */
  setPaused(paused: boolean) {
    if (this._paused === paused) return;
    this._paused = paused;
    if (paused) {
      if (this.ctx.state === 'running') void this.ctx.suspend();
    } else if (this.ctx.state === 'suspended') {
      void this.ctx.resume();
    }
  }

  // ── Playback ───────────────────────────────────────────────
  async playMusic(url: string, loop = true): Promise<void> {
    if (this._paused) return;
    if (this.currentMusicUrl === url) return; // already playing this track
    this.stopMusic();
    this.currentMusicUrl = url;
    await this.ctx.resume();
    const buf = await this.loadBuffer(url);
    if (this.currentMusicUrl !== url) return; // superseded while loading
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = loop;
    src.connect(this.musicMuteGain);
    src.start();
    this.currentMusic = src;
  }

  stopMusic() {
    if (this.currentMusic) {
      try { this.currentMusic.stop(); } catch { /* already ended */ }
      this.currentMusic = null;
    }
    this.currentMusicUrl = null;
  }

  /** Smoothly fade out current music over `duration` seconds, then stop */
  fadeOutMusic(duration: number) {
    if (!this.currentMusic) return;
    const gain = this.musicVolumeGain;
    gain.gain.setValueAtTime(gain.gain.value, this.ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + duration);
    // Schedule stop and restore volume after fade completes
    const music = this.currentMusic;
    setTimeout(() => {
      try { music.stop(); } catch { /* already ended */ }
      if (this.currentMusic === music) {
        this.currentMusic = null;
        this.currentMusicUrl = null;
      }
      // Restore gain to saved volume for next track
      gain.gain.cancelScheduledValues(this.ctx.currentTime);
      gain.gain.value = this._musicVolume;
    }, duration * 1000 + 50);
  }

  async playSound(url: string, volume: number = 1): Promise<void> {
    if (this._paused) return;
    await this.ctx.resume();
    const buf = await this.loadBuffer(url);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    if (volume !== 1) {
      const gain = this.ctx.createGain();
      gain.gain.value = volume;
      gain.connect(this.sfxMuteGain);
      src.connect(gain);
      // Disconnect nodes when playback ends to prevent Web Audio graph memory leak
      src.onended = () => { src.disconnect(); gain.disconnect(); };
    } else {
      src.connect(this.sfxMuteGain);
      src.onended = () => { src.disconnect(); };
    }
    src.start();
  }

  // ── Internal ───────────────────────────────────────────────
  private async loadBuffer(url: string): Promise<AudioBuffer> {
    const cached = this.bufferCache.get(url);
    if (cached) return cached;
    const res = await fetch(url);
    const raw = await res.arrayBuffer();
    const buf = await this.ctx.decodeAudioData(raw);
    this.bufferCache.set(url, buf);
    return buf;
  }
}
